/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-14
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  CelebrationOutlined,
  IntegrationInstructionsOutlined,
  SmartToyOutlined,
  BarChartOutlined,
  PersonOutlined,
  EmojiEventsOutlined,
  CalendarTodayOutlined,
  TrendingUpOutlined,
  FavoriteOutlined,
  BusinessOutlined,
  CheckroomOutlined,
  RocketLaunchOutlined,
  PaletteOutlined,
  CampaignOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import Header from '../../layout/Header';
import Footer from '../../layout/Footer';

// Define feature interface.
interface Feature {
  title: string;
  description: string;
  icon: React.ReactElement;
  status: 'coming-first' | 'coming-soon' | 'planned';
}

/**
 * Home page component.
 * @example
 * return (
 *   <Home />
 * )
 * @returns {React.ReactElement} component.
 */
const Home: React.FC = () => {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleViewPricing = () => {
    navigate('/pricing');
  };

  // Define features array with translation keys
  const featureKeys = [
    'gettingStarted',
    'studioPro',
    'financial',
    'teacherBios',
    'birthday',
    'studioTheme',
    'announcements',
    'schedules',
    'aiAssistant',
    'milestones',
    'leveling',
    'costume',
  ] as const;

  const featureIcons = [
    <RocketLaunchOutlined sx={{ fontSize: 40 }} />,
    <IntegrationInstructionsOutlined sx={{ fontSize: 40 }} />,
    <BarChartOutlined sx={{ fontSize: 40 }} />,
    <PersonOutlined sx={{ fontSize: 40 }} />,
    <CelebrationOutlined sx={{ fontSize: 40 }} />,
    <PaletteOutlined sx={{ fontSize: 40 }} />,
    <CampaignOutlined sx={{ fontSize: 40 }} />,
    <CalendarTodayOutlined sx={{ fontSize: 40 }} />,
    <SmartToyOutlined sx={{ fontSize: 40 }} />,
    <EmojiEventsOutlined sx={{ fontSize: 40 }} />,
    <TrendingUpOutlined sx={{ fontSize: 40 }} />,
    <CheckroomOutlined sx={{ fontSize: 40 }} />,
  ];

  const featureStatuses: Array<'coming-first' | 'coming-soon' | 'planned'> = [
    'coming-first',
    'coming-first',
    'coming-first',
    'coming-first',
    'coming-first',
    'coming-first',
    'coming-first',
    'coming-soon',
    'planned',
    'planned',
    'planned',
    'planned',
  ];

  const features: Feature[] = featureKeys.map((key, index) => ({
    title: t(`features.items.${key}.title`),
    description: t(`features.items.${key}.description`),
    icon: featureIcons[index],
    status: featureStatuses[index],
  }));

  // Old hardcoded features array removed
  /* const features: Feature[] = [
   */

  // Commented out for MVP launch - keep for potential rollback
  // const handleJoinWaitlist = () => {
  //   navigate('/waitlist');
  // };

  const getStatusChip = (status: Feature['status']) => {
    const statusConfig = {
      'coming-first': {
        label: t('features.status.ready'),
        color: 'primary' as const,
      },
      'coming-soon': {
        label: t('features.status.comingSoon'),
        color: 'secondary' as const,
      },
      planned: {
        label: t('features.status.planned'),
        color: 'default' as const,
      },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{ mb: 2 }}
      />
    );
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: {
                xs: '2.2rem',
                md: '3rem',
                lg: '3.5rem',
              },
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            {t('hero.title')}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: '1.5rem',
                md: '2rem',
              },
              mb: 2,
              color: 'primary.main',
            }}
          >
            {t('hero.tagline')}
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              mb: 4,
              fontSize: {
                xs: '1.1rem',
                md: '1.3rem',
              },
              lineHeight: 1.5,
            }}
          >
            {t('hero.description')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('hero.getStarted')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleViewPricing}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('hero.viewPricing')}
            </Button>
          </Box>
        </Box>

        {/* Welcome Video Section */}
        <Box sx={{ mb: 10 }}>
          <Box
            sx={{
              maxWidth: 900,
              mx: 'auto',
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              src="https://www.youtube.com/embed/_Aw9ph0xl_M"
              title="Welcome to tools.dance - Introduction Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        </Box>

        {/* We Get You Section - Credibility */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4 }}
          >
            {t('weGetYou.title')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              gap: 4,
              alignItems: 'center',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {t('weGetYou.subtitle')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}
              >
                {t('weGetYou.bio1')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}
              >
                {t('weGetYou.bio2')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '1.1rem', lineHeight: 1.7, fontWeight: 500 }}
              >
                {t('weGetYou.bio3')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  p: 4,
                  borderRadius: 3,
                  textAlign: 'center',
                  border: 2,
                  borderColor: 'primary.light',
                  boxShadow: 1,
                }}
              >
                <BusinessOutlined
                  sx={{ fontSize: 60, color: 'primary.main', mb: 2 }}
                />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('weGetYou.studioName')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('weGetYou.studioLocation')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('weGetYou.studioSince')}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, fontStyle: 'italic' }}>
                  &quot;{t('weGetYou.studioQuote')}&quot;
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* We Got You Section - Problems & Solutions */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4 }}
          >
            {t('weGotYou.title')}
          </Typography>
          <Typography
            variant="h5"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            {t('weGotYou.subtitle')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              gap: 4,
              justifyContent: 'center',
            }}
          >
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <FavoriteOutlined
                sx={{ fontSize: 50, color: 'primary.main', mb: 2 }}
              />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('weGotYou.cards.love.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('weGotYou.cards.love.description')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <IntegrationInstructionsOutlined
                sx={{ fontSize: 50, color: 'primary.main', mb: 2 }}
              />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('weGotYou.cards.integration.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('weGotYou.cards.integration.description')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <CalendarTodayOutlined
                sx={{ fontSize: 50, color: 'primary.main', mb: 2 }}
              />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('weGotYou.cards.setup.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('weGotYou.cards.setup.description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* You're Good Section - Features */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4 }}
          >
            {t('features.title')}
          </Typography>
          <Typography
            variant="h5"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 650, mx: 'auto' }}
          >
            {t('features.subtitle')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {features.map(feature => (
              <Card
                key={feature.title}
                tabIndex={0}
                role="article"
                aria-label={`${feature.title} - ${feature.description}`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition:
                    'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  cursor: 'default',
                  '&:hover, &:focus-visible': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  '&:focus-visible': {
                    outline: '3px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                }}
                onKeyDown={e => {
                  // Allow Enter or Space to "activate" the card (same as hover effect)
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Card is informational only, so we just ensure it's focused
                    e.currentTarget.focus();
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  {getStatusChip(feature.status)}
                  <Box
                    sx={{
                      color: 'primary.main',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Partnership Section */}
        <Box sx={{ mb: 10 }}>
          <Container maxWidth="md">
            <Box
              sx={{
                bgcolor: 'background.paper',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 700, mb: 3 }}
              >
                {t('partnership.title')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}
              >
                {t('partnership.paragraph1')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  fontStyle: 'italic',
                }}
              >
                &quot;{t('partnership.quote')}&quot;
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Final CTA Section */}
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            {t('finalCta.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            {t('finalCta.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('finalCta.button')}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('finalCta.note')}
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Home;
